import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  MicrophoneSettings,
  UpdateMicrophoneSettingsRequest,
} from '@speaksee/speaksee-angular-core';

interface MicrophoneDisplay extends MicrophoneSettings {
  isActive: boolean;
  displayName: string;
}
import { PUCK_COLORS_HEX, PUCK_COLORS_NAME_PAIR } from '../../constants/color';
import { Microphone } from '../../shared/models/microphone.model';
import { SettingsService } from '../../services/settings.service';
import { SpeechService } from '../../services/speech.service';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-microphone-settings',
  templateUrl: './microphone-settings.component.html',
  styleUrls: ['./microphone-settings.component.scss'],
})
export class MicrophoneSettingsComponent implements OnInit {
  private readonly settingsService = inject(SettingsService);
  private readonly speechService = inject(SpeechService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly showMessageMetadata$ =
    this.settingsService.showMessageMetadata$;
  public readonly kit = this.settingsService.getRemoteSetSettings();
  public readonly colorList = PUCK_COLORS_HEX;
  public readonly colorNames = PUCK_COLORS_NAME_PAIR;

  public readonly editingState = signal<{
    micId: number | null;
    name: string;
    color: string;
  }>({ micId: null, name: '', color: '' });

  public readonly allMicrophones = signal<MicrophoneSettings[]>([]);
  private readonly activeMicrophonesFromSpeech = signal<Microphone[]>([]);

  public readonly microphones = computed(() => {
    const settingsMics = this.allMicrophones();
    const speechMics = this.activeMicrophonesFromSpeech();
    const activeIds = new Set(speechMics.map((mic) => mic.id));

    return settingsMics.map((mic) => {
      const speechMic = speechMics.find((sm) => sm.id === mic.mic_id);
      const displayName = mic.name?.trim() || speechMic?.name || '';
      
      return {
        ...mic,
        isActive: activeIds.has(mic.mic_id),
        displayName: displayName
      };
    });
  });

  private readonly micUpdates: UpdateMicrophoneSettingsRequest = {
    microphones: [],
  };

  ngOnInit(): void {
    this.speechService.activeMics$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((activeMics) => {
        this.activeMicrophonesFromSpeech.set(activeMics);
      });

    this.kit.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((kit) => {
      if (kit?.microphones) {
        this.allMicrophones.set(kit.microphones);
      }
    });
  }

  protected toggleMetadata(): void {
    this.settingsService.toggleDisplayMetadata();
  }

  protected startEditing(micId: number): void {
    const mic = this.microphones().find((m) => m.mic_id === micId);
    if (mic) {
      this.editingState.set({
        micId: micId,
        name: mic.name || '',
        color: mic.color_hex,
      });
    }
  }

  protected cancelEditing(): void {
    this.editingState.set({ micId: null, name: '', color: '' });
  }

  protected updateEditingName(name: string): void {
    this.editingState.update((state) => ({ ...state, name }));
  }

  protected selectAllText(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  protected changeColor(mic: MicrophoneDisplay, color: string): void {
    this.editingState.update((state) => ({ ...state, color }));
    if (color !== mic.color_hex) {
      this.micSettingsChanged(mic.mic_id, color, mic.name || '');
    }
  }

  protected saveAndClose(mic: MicrophoneDisplay): void {
    const state = this.editingState();
    const currentName = state.name.trim();
    const originalName = mic.name || '';
    const nameChanged = currentName !== originalName;

    if (nameChanged) {
      this.micSettingsChanged(
        mic.mic_id,
        state.color || mic.color_hex,
        currentName
      );
    }
    this.cancelEditing();
  }

  protected async micSettingsChanged(
    micId: number,
    color: string,
    name: string
  ): Promise<void> {
    const currentMicrophones = this.allMicrophones();
    const micIndex = currentMicrophones.findIndex(
      (mic) => mic.mic_id === micId
    );

    if (micIndex !== -1) {
      const updatedMicrophones = [...currentMicrophones];
      updatedMicrophones[micIndex] = {
        ...updatedMicrophones[micIndex],
        color_hex: color,
        name: name,
      };
      this.allMicrophones.set(updatedMicrophones);
    }

    const existingUpdate = this.micUpdates.microphones.find(
      (mic) => mic.mic_id === micId
    );

    if (existingUpdate) {
      existingUpdate.color_hex = color;
      existingUpdate.name = name;
    } else {
      this.micUpdates.microphones.push({
        mic_id: micId,
        color_hex: color,
        name: name,
        language_out: null,
        language_in: null,
      } as MicrophoneSettings);
    }

    this.settingsService
      .updateMicSettings(this.micUpdates)
      .pipe(take(1))
      .subscribe({
        error: (err) =>
          console.error('Failed to update microphone settings:', err),
      });
  }
}
